import { useState, useEffect, useRef } from 'react'
import {
    PhoneCall, RefreshCw, Power, FileCode, Bug, CheckCircle2, XCircle,
    AlertTriangle, Loader2, Server, Radio, Activity, FileAudio, Download,
    FileText, ChevronDown, ChevronRight
} from 'lucide-react'
import { asteriskBotApi } from '../services/api'

const POLL_MS = 5000

function StatusPill({ ok, label, warnIfFalse = true }) {
    const color = ok ? '#22c55e' : (warnIfFalse ? '#ef4444' : '#6b7280')
    const Icon = ok ? CheckCircle2 : (warnIfFalse ? XCircle : AlertTriangle)
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '4px 10px', borderRadius: 999,
            background: color + '22', color, fontSize: 13, fontWeight: 500
        }}>
            <Icon size={14} /> {label}
        </span>
    )
}

function Card({ title, icon: Icon, children, action }) {
    return (
        <div style={{
            background: '#1a1d23',
            border: '1px solid #2a2e36',
            color: '#e5e7eb',
            borderRadius: 8, padding: 16, marginBottom: 16
        }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0, fontSize: 16, fontWeight: 600, color: '#f9fafb' }}>
                    {Icon && <Icon size={18} />}
                    {title}
                </h3>
                {action}
            </div>
            {children}
        </div>
    )
}

function RecordingRow({ rec }) {
    const [openTr, setOpenTr] = useState(false)
    const [openLog, setOpenLog] = useState(false)
    const [transcript, setTranscript] = useState(null)
    const [callLog, setCallLog] = useState(null)
    const [loadingTr, setLoadingTr] = useState(false)
    const [loadingLog, setLoadingLog] = useState(false)
    async function loadTranscript() {
        if (transcript !== null) return
        setLoadingTr(true)
        try {
            const r = await asteriskBotApi.getTranscript(rec.transcript)
            setTranscript(r.data.transcript || '(empty)')
        } catch (e) { setTranscript(`(error: ${e.message})`) }
        finally { setLoadingTr(false) }
    }
    async function loadLog() {
        if (callLog !== null) return
        setLoadingLog(true)
        try {
            const r = await asteriskBotApi.getCallLog(rec.filename)
            setCallLog(r.data.log || '(no log lines for this call)')
        } catch (e) { setCallLog(`(error: ${e.message})`) }
        finally { setLoadingLog(false) }
    }
    return (
        <div style={{ borderBottom: '1px solid #2a2e36', padding: '10px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {rec.filename}
                    </div>
                    <div style={{ fontSize: 11, opacity: 0.6, marginTop: 2 }}>
                        {rec.modified} · {(rec.size / 1024).toFixed(1)} KB · {((rec.size - 44) / 16000).toFixed(1)}s
                        {rec.transcript && <span style={{ color: '#22c55e', marginLeft: 8 }}>· transcript ready</span>}
                    </div>
                </div>
                <audio controls preload="none" src={asteriskBotApi.recordingUrl(rec.filename)}
                       style={{ height: 32, maxWidth: 280 }} />
                <a href={asteriskBotApi.recordingUrl(rec.filename)} download={rec.filename}
                   style={{ color: '#3b82f6', display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none', fontSize: 13 }}>
                    <Download size={14} /> WAV
                </a>
                {rec.transcript && (
                    <button
                        onClick={() => { if (!openTr) loadTranscript(); setOpenTr(!openTr) }}
                        style={{ background: 'transparent', border: '1px solid #3b82f6', color: '#3b82f6',
                                 padding: '4px 8px', borderRadius: 4, cursor: 'pointer',
                                 display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                        {openTr ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        <FileText size={14} /> Transcript
                    </button>
                )}
                <button
                    onClick={() => { if (!openLog) loadLog(); setOpenLog(!openLog) }}
                    style={{ background: 'transparent', border: '1px solid #a855f7', color: '#a855f7',
                             padding: '4px 8px', borderRadius: 4, cursor: 'pointer',
                             display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                    {openLog ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <FileCode size={14} /> Bot log
                </button>
            </div>
            {openTr && (
                <div style={{
                    marginTop: 10, padding: 10, background: '#0d1117',
                    borderRadius: 4, fontSize: 13, lineHeight: 1.5,
                    whiteSpace: 'pre-wrap', maxHeight: 300, overflow: 'auto'
                }}>
                    {loadingTr ? <Loader2 className="animate-spin" size={14} /> : transcript || '(loading)'}
                </div>
            )}
            {openLog && (
                <div style={{
                    marginTop: 10, padding: 10, background: '#0d1117',
                    borderRadius: 4, fontSize: 12, lineHeight: 1.4,
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    maxHeight: 400, overflow: 'auto', fontFamily: 'monospace'
                }}>
                    {loadingLog ? <Loader2 className="animate-spin" size={14} /> : callLog || '(loading)'}
                </div>
            )}
        </div>
    )
}

function ActionButton({ icon: Icon, label, onClick, busy, danger, disabled }) {
    return (
        <button
            onClick={onClick}
            disabled={busy || disabled}
            style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 14px', borderRadius: 6,
                background: danger ? '#dc2626' : '#3b82f6',
                color: 'white', border: 'none',
                cursor: busy || disabled ? 'not-allowed' : 'pointer',
                opacity: busy || disabled ? 0.6 : 1,
                fontSize: 14, fontWeight: 500
            }}
        >
            {busy ? <Loader2 size={14} className="animate-spin" /> : Icon && <Icon size={14} />}
            {label}
        </button>
    )
}

export default function AsteriskBot() {
    const [status, setStatus] = useState(null)
    const [log, setLog] = useState('')
    const [logWhich, setLogWhich] = useState('bot')
    const [logLines, setLogLines] = useState(100)
    const [recordings, setRecordings] = useState([])
    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(true)
    const [busy, setBusy] = useState(null)
    const [autoRefresh, setAutoRefresh] = useState(true)
    const [toast, setToast] = useState(null)
    const logEndRef = useRef(null)

    async function loadStatus() {
        try {
            const r = await asteriskBotApi.getStatus()
            setStatus(r.data)
            setError(null)
        } catch (e) {
            setError(e.response?.data?.error || e.message)
        } finally {
            setLoading(false)
        }
    }

    async function loadLog() {
        try {
            const r = await asteriskBotApi.getLog(logWhich, logLines)
            setLog(r.data.log || '')
        } catch (e) {
            console.error('log fetch failed', e)
        }
    }

    async function loadRecordings() {
        try {
            const r = await asteriskBotApi.listRecordings()
            setRecordings(r.data.recordings || [])
        } catch (e) {
            console.error('recordings fetch failed', e)
        }
    }

    useEffect(() => {
        loadStatus()
        loadLog()
        loadRecordings()
    }, []) // initial

    useEffect(() => {
        loadLog()
    }, [logWhich, logLines])

    useEffect(() => {
        if (!autoRefresh) return
        const id = setInterval(() => {
            loadStatus()
            loadLog()
            loadRecordings()
        }, POLL_MS)
        return () => clearInterval(id)
    }, [autoRefresh, logWhich, logLines])

    function flash(msg, ok = true) {
        setToast({ msg, ok })
        setTimeout(() => setToast(null), 3500)
    }

    async function doAction(name, fn) {
        setBusy(name)
        try {
            const r = await fn()
            flash(r.data.output || 'OK', r.data.success !== false)
            await loadStatus()
            await loadLog()
        } catch (e) {
            flash(e.response?.data?.error || e.message, false)
        } finally {
            setBusy(null)
        }
    }

    if (loading) {
        return <div style={{ padding: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Loader2 className="animate-spin" /> Loading bot status...
        </div>
    }

    const peer = status?.peers?.[0]
    const reg = status?.registry?.[0]
    const channels = status?.channels

    return (
        <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                <h1 style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 0 }}>
                    <Radio size={26} /> Asterisk / AudioSocket Bot
                </h1>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, opacity: 0.8 }}>
                        <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
                        Auto-refresh ({POLL_MS / 1000}s)
                    </label>
                    <ActionButton icon={RefreshCw} label="Refresh now" onClick={() => { loadStatus(); loadLog(); }} busy={busy === 'refresh'} />
                </div>
            </div>

            {error && (
                <div style={{
                    padding: 12, borderRadius: 6, marginBottom: 16,
                    background: '#dc262622', color: '#fca5a5', border: '1px solid #dc2626'
                }}>
                    <strong>Error:</strong> {error}
                </div>
            )}

            {toast && (
                <div style={{
                    position: 'fixed', top: 20, right: 20, zIndex: 1000,
                    padding: '12px 18px', borderRadius: 6,
                    background: toast.ok ? '#22c55e' : '#dc2626', color: 'white',
                    fontSize: 14, fontWeight: 500, maxWidth: 400,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }}>
                    {toast.msg}
                </div>
            )}

            <Card title="Service health" icon={Server}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    <StatusPill ok={status?.asterisk?.active} label={`Asterisk: ${status?.asterisk?.active ? 'active' : 'down'}`} />
                    <StatusPill ok={status?.bot?.active} label={`Bot service: ${status?.bot?.active ? 'active' : 'down'}`} />
                    <StatusPill ok={status?.bot?.listening} label={`Port 9999: ${status?.bot?.listening ? 'listening' : 'closed'}`} />
                </div>
                {status?.asterisk?.uptime && (
                    <div style={{ marginTop: 10, fontSize: 13, opacity: 0.75, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                        {status.asterisk.uptime}
                    </div>
                )}
            </Card>

            <Card title="SIP peer (Vicidial)" icon={PhoneCall}>
                {peer ? (
                    <div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 14 }}>
                            <div><strong>Name:</strong> {peer.name}</div>
                            <div><strong>Host:</strong> {peer.host}</div>
                            <div><strong>Port:</strong> {peer.port}</div>
                        </div>
                        <div style={{ marginTop: 8 }}>
                            <StatusPill
                                ok={/OK/i.test(peer.status)}
                                label={`Status: ${peer.status}`}
                            />
                        </div>
                    </div>
                ) : <div style={{ opacity: 0.6 }}>No peers configured.</div>}

                {reg && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #2a2e36' }}>
                        <div style={{ fontSize: 13, opacity: 0.8 }}>
                            <strong>Outbound registration:</strong> {reg.username}@{reg.host} —
                            <span style={{ marginLeft: 6, color: /Registered/i.test(reg.state) ? '#22c55e' : '#f59e0b' }}>
                                {reg.state}
                            </span>
                            <span style={{ marginLeft: 8, opacity: 0.6 }}>(refresh {reg.refresh}s)</span>
                        </div>
                    </div>
                )}
            </Card>

            <Card title="Active channels" icon={Activity}>
                <div style={{ display: 'flex', gap: 16, marginBottom: 12, fontSize: 14 }}>
                    <div><strong>Active:</strong> {channels?.active ?? 0}</div>
                    <div><strong>In call:</strong> {channels?.calls ?? 0}</div>
                    <div><strong>Total processed:</strong> {channels?.processed ?? 0}</div>
                </div>
                {channels?.list?.length > 0 ? (
                    <table style={{ width: '100%', fontSize: 13, fontFamily: 'monospace' }}>
                        <thead>
                            <tr style={{ opacity: 0.7, textAlign: 'left' }}>
                                <th>Channel</th><th>Location</th><th>State</th><th>App</th>
                            </tr>
                        </thead>
                        <tbody>
                            {channels.list.map((c, i) => (
                                <tr key={i} style={{ borderTop: '1px solid #2a2e36' }}>
                                    <td>{c.channel}</td><td>{c.location}</td><td>{c.state}</td><td>{c.application}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : <div style={{ opacity: 0.6, fontSize: 13 }}>No active channels right now.</div>}
            </Card>

            <Card title="Actions">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    <ActionButton
                        icon={RefreshCw} label="Reload dialplan"
                        onClick={() => doAction('reload', asteriskBotApi.reloadDialplan)}
                        busy={busy === 'reload'} />
                    <ActionButton
                        icon={RefreshCw} label="Reload bot config from dashboard"
                        onClick={() => doAction('reload-config', asteriskBotApi.reloadConfig)}
                        busy={busy === 'reload-config'} />
                    <ActionButton
                        icon={Power} label="Restart bot"
                        onClick={() => doAction('restart-bot', asteriskBotApi.restartBot)}
                        busy={busy === 'restart-bot'} />
                    <ActionButton
                        icon={Power} label="Restart Asterisk" danger
                        onClick={() => {
                            if (window.confirm('Restart Asterisk in LXC 400? This will drop any active calls.')) {
                                doAction('restart-asterisk', asteriskBotApi.restartAsterisk)
                            }
                        }}
                        busy={busy === 'restart-asterisk'} />
                    <ActionButton
                        icon={Bug} label="SIP debug ON"
                        onClick={() => doAction('debug-on', () => asteriskBotApi.setSipDebug(true))}
                        busy={busy === 'debug-on'} />
                    <ActionButton
                        icon={Bug} label="SIP debug OFF"
                        onClick={() => doAction('debug-off', () => asteriskBotApi.setSipDebug(false))}
                        busy={busy === 'debug-off'} />
                </div>
            </Card>

            <Card
                title={`Call recordings (${recordings.length})`}
                icon={FileAudio}
                action={
                    <ActionButton icon={RefreshCw} label="Reload" onClick={loadRecordings} busy={busy === 'rec'} />
                }
            >
                {recordings.length === 0 ? (
                    <div style={{ opacity: 0.6, fontSize: 13 }}>
                        No recordings yet. Files appear here after a call connects.
                    </div>
                ) : (
                    <div style={{ maxHeight: 600, overflow: 'auto' }}>
                        {recordings.map((r) => (
                            <RecordingRow key={r.filename} rec={r} />
                        ))}
                    </div>
                )}
            </Card>

            <Card
                title="Log tail"
                icon={FileCode}
                action={
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <select value={logWhich} onChange={(e) => setLogWhich(e.target.value)}
                            style={{ background: '#0d1117', color: '#e5e7eb', border: '1px solid #2a2e36', padding: '4px 8px', borderRadius: 4 }}>
                            <option value="bot">audiosocket-bot.log</option>
                            <option value="asterisk">asterisk/messages</option>
                        </select>
                        <select value={logLines} onChange={(e) => setLogLines(parseInt(e.target.value))}
                            style={{ background: '#0d1117', color: '#e5e7eb', border: '1px solid #2a2e36', padding: '4px 8px', borderRadius: 4 }}>
                            <option value={50}>50 lines</option>
                            <option value={100}>100 lines</option>
                            <option value={250}>250 lines</option>
                            <option value={500}>500 lines</option>
                        </select>
                    </div>
                }
            >
                <pre style={{
                    background: '#0d1117', color: '#c9d1d9',
                    padding: 12, borderRadius: 4, maxHeight: 400, overflow: 'auto',
                    fontSize: 12, lineHeight: 1.45, margin: 0,
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                }}>
                    {log || '(no log content)'}
                    <div ref={logEndRef} />
                </pre>
            </Card>

            <div style={{ fontSize: 12, opacity: 0.5, textAlign: 'center', padding: 12 }}>
                Connected via SSH to {status?.host} → LXC {status?.vmid}
            </div>
        </div>
    )
}
